import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { logger } from "@/infrastructure/services/LoggerService";
import { getNotificationRoles, getNotificationUser, searchNotificationUsers, } from "../infrastructure/adapters/taxReceiptNotificationRecipientsService";
/**
 * The recipients picker is held back until OPUS exposes a directory endpoint open to `opdon.edit`
 * users: today its three routes sit behind the AccessManager policy (OP-7690 comment of 2026-09-28).
 * Off, the picker is not rendered and the directory is not fetched; the state, validation and
 * save payload stay wired.
 */
export const TAX_RECEIPT_RECIPIENTS_PICKER_ENABLED = false;
/**
 * The campaign notification picker mixes two OPDON lists (Keycloak user ids and role names) in
 * one `MultipleSelect`. Option ids carry their list as a prefix so the split back is lossless.
 */
export const USER_OPTION_PREFIX = "user:";
export const ROLE_OPTION_PREFIX = "role:";
export function toRecipientOptionIds(userIds, roles) {
    return [
        ...roles.map((role) => `${ROLE_OPTION_PREFIX}${role}`),
        ...userIds.map((id) => `${USER_OPTION_PREFIX}${id}`),
    ];
}
export function fromRecipientOptionIds(optionIds) {
    const userIds = [];
    const roles = [];
    for (const optionId of optionIds) {
        if (optionId.startsWith(USER_OPTION_PREFIX))
            userIds.push(optionId.slice(USER_OPTION_PREFIX.length));
        else if (optionId.startsWith(ROLE_OPTION_PREFIX))
            roles.push(optionId.slice(ROLE_OPTION_PREFIX.length));
    }
    return { userIds, roles };
}
export function useTaxReceiptNotificationRecipients() {
    const { t } = useI18n();
    const T = "finance.taxReceipts.settings.trigger";
    const roles = ref([]);
    // Every user the picker has ever shown or that the settings reference: `MultipleSelect`
    // renders a chip only for an id present in `options`, so a selected user must stay listed
    // after the search results moved on.
    const knownUsers = ref(new Map());
    const searchResultIds = ref([]);
    const isLoading = ref(false);
    const isSearching = ref(false);
    const loadError = ref(false);
    const options = computed(() => {
        const roleOptions = roles.value.map((role) => ({
            id: `${ROLE_OPTION_PREFIX}${role.type}`,
            label: role.label,
            secondaryText: t(`${T}.recipientRole`),
        }));
        const userOptions = searchResultIds.value
            .map((id) => knownUsers.value.get(id))
            .filter((user) => user !== undefined)
            .map((user) => ({
            id: `${USER_OPTION_PREFIX}${user.id}`,
            label: user.name || user.email,
            secondaryText: user.email || t(`${T}.recipientUser`),
        }));
        return [...roleOptions, ...userOptions];
    });
    /** Options for ids the current search page does not list, so their chips keep a label. */
    function optionsForSelected(selectedOptionIds) {
        const listed = new Set(options.value.map((option) => option.id));
        const { userIds, roles: roleTypes } = fromRecipientOptionIds(selectedOptionIds);
        // A role the catalogue no longer lists keeps a chip too, or it would be re-sent unseen.
        const roleOptions = roleTypes
            .filter((type) => !listed.has(`${ROLE_OPTION_PREFIX}${type}`))
            .map((type) => ({
            id: `${ROLE_OPTION_PREFIX}${type}`,
            label: t(`${T}.recipientUnknownRole`),
            secondaryText: t(`${T}.recipientRole`),
        }));
        const userOptions = userIds
            .filter((id) => !listed.has(`${USER_OPTION_PREFIX}${id}`))
            .map((id) => {
            const user = knownUsers.value.get(id);
            return {
                id: `${USER_OPTION_PREFIX}${id}`,
                label: user ? user.name || user.email : t(`${T}.recipientUnknownUser`),
                secondaryText: user?.email ?? t(`${T}.recipientUser`),
            };
        });
        return [...roleOptions, ...userOptions];
    }
    function remember(users) {
        const next = new Map(knownUsers.value);
        for (const user of users)
            next.set(user.id, user);
        knownUsers.value = next;
    }
    async function load(selectedUserIds) {
        isLoading.value = true;
        loadError.value = false;
        try {
            const [roleList, firstPage] = await Promise.all([getNotificationRoles(), searchNotificationUsers()]);
            roles.value = roleList;
            remember(firstPage);
            searchResultIds.value = firstPage.map((user) => user.id);
            // A selected user beyond the first page is fetched by id; a deleted one keeps its id
            // (labelled unknown) so a save does not silently drop a recipient the user never touched.
            const missing = selectedUserIds.filter((id) => !knownUsers.value.has(id));
            const fetched = await Promise.allSettled(missing.map((id) => getNotificationUser(id)));
            remember(fetched
                .filter((r) => r.status === "fulfilled")
                .map((r) => r.value));
        }
        catch (err) {
            logger.error("Failed to load tax-receipt notification recipients", err);
            loadError.value = true;
        }
        finally {
            isLoading.value = false;
        }
    }
    // R97: only the latest in-flight search may write the results.
    let searchSequence = 0;
    async function search(query) {
        const sequence = ++searchSequence;
        isSearching.value = true;
        try {
            const users = await searchNotificationUsers(query.trim() || undefined);
            if (sequence !== searchSequence)
                return;
            remember(users);
            searchResultIds.value = users.map((user) => user.id);
        }
        catch (err) {
            if (sequence !== searchSequence)
                return;
            logger.error("Failed to search tax-receipt notification recipients", err);
        }
        finally {
            if (sequence === searchSequence)
                isSearching.value = false;
        }
    }
    return { options, optionsForSelected, isLoading, isSearching, loadError, load, search };
}
